import { useEffect, useState } from "react";

/**
 * Loading the ChatKit web component, which nothing else in the bundle does.
 *
 * `@openai/chatkit` SHIPS NO JAVASCRIPT. Its own README calls it "Type
 * declarations for the ChatKit Web Component", and its package `files` are
 * exactly `types` and `README.md`. So `<openai-chatkit>` is never registered by
 * installing it, and a `<ChatKit control={...} />` rendered without the CDN
 * bundle produces an unupgraded custom element: a box with no content, no
 * composer and no error. That is not a hypothetical - it is what a student saw
 * on a task, and it looked like a chat that had failed to load its messages
 * rather than a script that was never fetched.
 *
 * ChatKitPanel and PreviewAgentButton each load it themselves, which is why
 * ChatKit works on the agent page and nowhere else. This is the third copy of
 * that idea and the first shared one; the other two are left alone here rather
 * than refactored under a bug fix, and should adopt this.
 */
const CHATKIT_SRC = "https://cdn.platform.openai.com/deployments/chatkit/chatkit.js";
const ELEMENT = "openai-chatkit";

/**
 * How long after the script's `load` event the element may take to register.
 *
 * Deliberately generous. A healthy bundle defines the element within microtasks
 * of loading, so this is never reached in the normal case; it is the escape
 * from a bundle that loads and never registers, which `whenDefined` alone would
 * turn into a permanent spinner. Timing anything shorter would risk failing a
 * slow-but-working define for no gain.
 */
const REGISTRATION_TIMEOUT_MS = 10_000;

export type ChatKitScriptState = "loading" | "ready" | "failed";

/**
 * The in-flight load, shared by every caller.
 *
 * Two surfaces can mount at once, and each must get the SAME attempt: one
 * script tag, one set of listeners, one outcome. Cleared on failure so the next
 * caller starts a fresh attempt rather than inheriting a dead one. Not cleared
 * on success - once the element is defined, the fast path above never reaches
 * here again.
 */
let pendingLoad: Promise<void> | null = null;

/**
 * Resolve once `<openai-chatkit>` is defined, or reject if it cannot be.
 *
 * Three entry states, because a second surface must not inject a second script
 * tag: already defined, already injected by somebody else (adopt it), or
 * genuinely absent (inject it). `customElements.whenDefined` is the settle
 * condition rather than the script's `load` event - the element is what the
 * caller needs, and a loaded script that failed to register would otherwise
 * report success.
 *
 * THE DEADLOCK THIS AVOIDS: waiting on `whenDefined` ALONE. That promise never
 * rejects and never times out, so if the script errors - a school network
 * blocking the CDN is the likeliest cause - every waiter pends forever and the
 * surface spins with no error to show. The adopt path was the worst of it: it
 * attached no error listener at all, so a tag that failed after a second
 * surface mounted took that surface down silently. Both paths now attach `load`
 * AND `error`, and a failure rejects every waiter, drops the dead tag, and
 * clears `pendingLoad` so the next call can genuinely retry.
 *
 * The second half of the same deadlock is a script that LOADS and then does not
 * register the element - a bad build, a CSP blocking its eval, an upstream
 * regression. `load` fires, `error` does not, and `whenDefined` waits for a
 * definition that is never coming, so again nothing settles. Registration is
 * therefore checked after `load` and bounded by REGISTRATION_TIMEOUT_MS, which
 * fails down the same path as a fetch error: reject the waiters, drop the tag,
 * clear the promise, retry available.
 *
 * Residual, deliberately not guessed at: a foreign tag that ALREADY finished -
 * errored, or loaded without registering - before this module ran fires no
 * further events, and nothing in the DOM records which. The bound above hangs
 * off our own `load` and so does not cover it, on purpose: starting a timer at
 * call time would mean removing somebody else's tag while it was still
 * legitimately downloading. Removing tags only once they have finished means our
 * retries never adopt a corpse; ChatKitPanel and PreviewAgentButton still inject
 * their own copies, and should adopt this loader.
 */
export function ensureChatKitScript(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("no document"));
  }
  if (window.customElements?.get(ELEMENT)) return Promise.resolve();
  if (pendingLoad) return pendingLoad;

  pendingLoad = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src*="chatkit.js"]`
    );
    const script = existing ?? document.createElement("script");
    if (!existing) {
      script.src = CHATKIT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    let done = false;
    let registrationTimer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
      if (registrationTimer !== undefined) clearTimeout(registrationTimer);
    };
    const succeed = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };
    const fail = (error: Error) => {
      if (done) return;
      done = true;
      cleanup();
      // Drop the dead tag and the shared promise together, so the next caller
      // injects a fresh script instead of adopting this one and waiting on a
      // load event that will never come again.
      script.remove();
      pendingLoad = null;
      reject(error);
    };

    function onError() {
      fail(new Error("ChatKit script failed to load"));
    }
    function onLoad() {
      // Loaded is not defined: the bundle still has to register the element.
      // If it did so during evaluation there is nothing left to wait for.
      if (window.customElements.get(ELEMENT)) {
        succeed();
        return;
      }

      // `whenDefined` never rejects and never times out, so a bundle that
      // downloads and then fails to register - a bad build, a CSP that blocks
      // its eval, an upstream regression - would settle NEITHER branch: every
      // waiter pends for good and the shared promise never clears, so no
      // surface can even retry. The bound exists only for that path. It starts
      // at `load`, never at call time, so a slow download is never cut short;
      // by the time it runs the bytes have arrived and registration is a
      // microtask away, which is why ten seconds is generous rather than tight.
      registrationTimer = setTimeout(() => {
        fail(
          new Error(
            `ChatKit script loaded but <${ELEMENT}> was never registered`
          )
        );
      }, REGISTRATION_TIMEOUT_MS);

      window.customElements
        .whenDefined(ELEMENT)
        .then(succeed, () => fail(new Error("ChatKit element never registered")));
    }

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);

    // An adopted tag may have finished loading before these listeners were
    // attached, in which case its `load` event will never fire again and this
    // is the only signal left. It is also the real settle condition on the
    // happy path, so both entry states end here.
    window.customElements
      .whenDefined(ELEMENT)
      .then(succeed, () => fail(new Error("ChatKit element never registered")));
  });

  return pendingLoad;
}

/**
 * The script's state, for a surface that has to render something either way.
 *
 * A caller that mounted before the element exists must not mount the widget:
 * ChatKit binds its control methods when the element upgrades, so rendering
 * early is how a chat ends up with no composer to focus.
 */
export function useChatKitScript(): ChatKitScriptState {
  const [state, setState] = useState<ChatKitScriptState>(() =>
    typeof window !== "undefined" && window.customElements?.get(ELEMENT)
      ? "ready"
      : "loading"
  );

  useEffect(() => {
    if (state === "ready") return;
    let alive = true;
    ensureChatKitScript().then(
      () => alive && setState("ready"),
      () => alive && setState("failed")
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
